import axios from 'axios';
import { getAuthHeader } from '../../utils/authHeader';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetch dashboard data with pagination, sorting, and search
 */
export const fetchDashboardDataAPI = async (
  paginationInfo: { page: number; limit: number },
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
  searchTerm?: string,
  signal?: AbortSignal
) => {
  const headers = await getAuthHeader();
  
  let url = `${backendUrl}/admin/dashboard?page=${paginationInfo.page}&limit=${paginationInfo.limit || 10}`;
  if (sortBy) url += `&sortBy=${sortBy}`;
  if (sortDirection) url += `&sortOrder=${sortDirection}`;
  
  const trimmedSearch = searchTerm?.trim();
  if (trimmedSearch && trimmedSearch.length > 0) {
    url += `&search=${encodeURIComponent(trimmedSearch)}`;
  }
  
  return axios.get(url, { headers, signal });
};

/**
 * Fetch dashboard statistics (user count)
 */
export const fetchDashboardStatsAPI = async () => {
  const headers = await getAuthHeader();
  
  return axios.get(`${backendUrl}/admin/dashboard/user-count`, { headers });
};

/**
 * Fetch posts for a specific campaign with pagination and optional sorting
 */
export const fetchCampaignPostsAPI = async (
  campaignId: string,
  page: number,
  sortColumn?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  const headers = await getAuthHeader();
  
  let url = `${backendUrl}/admin/campaigns/${campaignId}?page=${page}&limit=10`;
  if (sortColumn) url += `&sortBy=${sortColumn}`;
  if (sortDirection) url += `&sortOrder=${sortDirection}`;
  
  return axios.get(url, { headers });
};

/**
 * Fetch posts for a promotion campaign with pagination and optional sorting
 */
export const fetchPromotionPostsAPI = async (
  campaignId: string,
  page: number,
  sortColumn?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  const headers = await getAuthHeader();
  
  let url = `${backendUrl}/admin/campaigns/${campaignId}?page=${page}&limit=10`;
  if (sortColumn) url += `&sortBy=${sortColumn}`;
  if (sortDirection) url += `&sortOrder=${sortDirection}`;
  
  return axios.get(url, { headers });
};

/**
 * Fetch detailed information for a specific user
 */
export const fetchUserDetailsAPI = async (
  userId: string,
  sortColumn?: string,
  sortDirection?: 'asc' | 'desc',
  page: number = 1,
  leadGenPage: number = 1,
  leadGenSortColumn?: string,
  leadGenSortDirection?: 'asc' | 'desc'
) => {
  const headers = await getAuthHeader();
  
  let url = `${backendUrl}/admin/users/${userId}?`;
  
  // Determine what type of request this is and build URL accordingly
  if (leadGenSortColumn) {
    // Lead Generation sorting request - only include leadGen parameters
    url += `leadGenPage=${page}&leadGenLimit=10`;
    url += `&leadGenSortBy=${leadGenSortColumn}`;
    if (leadGenSortDirection) url += `&leadGenSortOrder=${leadGenSortDirection}`;
  } else if (sortColumn) {
    // MyBrand sorting request - only include myBrand parameters
    url += `myBrandPage=${page}&myBrandLimit=10`;
    url += `&myBrandSortBy=${sortColumn}`;
    if (sortDirection) url += `&myBrandSortOrder=${sortDirection}`;
  } else {
    // General fetch request - include both to get all user data
    url += `myBrandPage=${page}&myBrandLimit=10&leadGenPage=${leadGenPage}&leadGenLimit=10`;
  }
  
  return axios.get(url, { headers });
};

/**
 * Handle removal of items (listing, post, promo)
 */
export const handleRemoveAPI = async (
  type: string,
  id: string,
  TypeOfListing?: string
) => {
  const headers = await getAuthHeader();
  
  if (type === 'listing') {
    return axios.post(
      `${backendUrl}/listing/delete`,
      {
        listingId: id,
        listingType: TypeOfListing
      },
      { headers }
    );
  } else if (type === 'post') {
    return axios.delete(`${backendUrl}/post/${id}`, { headers });
  } else if (type === 'promo') {
    return axios.delete(`${backendUrl}/promotions/${id}`, { headers });
  }
  
  throw new Error(`Unsupported removal type: ${type}`);
};

/**
 * Pause or resume a campaign
 */
export const handlePauseCampaignAPI = async (
  campaignId: string,
  newStatus: string,
  type?: string
) => {
  const headers = await getAuthHeader();
  
  const listingCampaignEndpoint = `${backendUrl}/listing/campaign/${campaignId}`;
  const promotionCampaignEndpoint = `${backendUrl}/promotions/campaign/${campaignId}`;
  const endpoint = type === 'promo' ? promotionCampaignEndpoint : listingCampaignEndpoint;
  
  return axios.patch(endpoint, { status: newStatus }, { headers });
};

/**
 * Save schedule for posts
 */
export const handleSaveScheduleAPI = async (
  postIds: string[],
  scheduledFor: string
) => {
  const headers = await getAuthHeader();
  
  const payload = {
    postIds,
    scheduledFor
  };
  
  return axios.patch(`${backendUrl}/post`, payload, { headers });
};

/**
 * Fetch Cloud Run versions
 */
export const fetchCloudRunVersionsAPI = async () => {
  const headers = await getAuthHeader();
  return axios.get(`${backendUrl}/admin/cloudrun-versions`, { headers });
};